import { useContextState } from '../../../../../../../hooks/theme'
import { SxProps, Theme } from '@mui/system'

export const getResultsStyles = () => {
  const { colors: color } = useContextState()

  const tabsContainer: SxProps<Theme> = {
    display: 'flex',
    gap: '8px',
    backgroundColor: `transparent`,
    border: `1px solid ${color.color10}50`,
    borderRadius: '6px',
    padding: '10px',
    mb: '16px',
  }

  const tabButton: SxProps<Theme> = {
    flex: 1,
    textAlign: 'center',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 500,
    color: color.color13,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: `${color.color10}22`,
    },
  }

  const tabButtonActive: SxProps<Theme> = {
    ...tabButton,
    backgroundColor: color.color45,
    color: color.color5,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: `${color.color45}`,
    },
  }

  const resultsLayout: SxProps<Theme> = {
    display: 'flex',
    gap: '16px',
    minHeight: '380px',
    flexWrap: 'wrap',
  }

  const questionnaireListPanel: SxProps<Theme> = {
    width: '280px',
    minWidth: '280px',
    height: '400px',
    maxHeight: '400px',
    borderRadius: '4px',
    boxShadow: `0 0 2px 0px ${color.color13}`,
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    paddingBottom: '12px',
  }

  const questionnaireListHeader: SxProps<Theme> = {
    padding: '12px 16px 8px',
    borderBottom: `1px solid ${color.color10}40`,
    backgroundColor: `${color.color10}10`,
    fontSize: '15px',
    fontWeight: 500,
    color: color.color13,
  }

  const questionnaireListSearch: SxProps<Theme> = {
    paddingTop: '12px',
    margin: '0 12px 6px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '2px',
      fontSize: '13px',
      height: '38px',
      backgroundColor: `transparent`,
    },
  }

  const questionnaireItem: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 8px',
    margin: '0 12px 6px',
    cursor: 'pointer',
    gap: '10px',
    borderRadius: '2px',
    border: `1px solid ${color.color10}40`,
    backgroundColor: '#fff',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: `${color.color10}10`,
      borderColor: `${color.color10}80`,
    },
    '& .MuiTypography-root': {
      color: color.color13,
    },
  }

  const questionnaireItemActive: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 8px',
    margin: '0 12px 6px',
    cursor: 'pointer',
    gap: '10px',
    borderRadius: '2px',
    border: `1px solid ${color.color16}`,
    backgroundColor: color.color45,
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: color.color45,
    },
    '& .MuiTypography-root': {
      color: '#fff',
    },
  }

  const questionnaireItemSelected: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 8px',
    margin: '0 12px 6px',
    cursor: 'pointer',
    gap: '10px',
    borderRadius: '2px',
    border: `1px solid ${color.color45}`,
    backgroundColor: 'transparent',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: `${color.color45}08`,
    },
    '& .MuiTypography-root': {
      color: color.color45,
    },
  }

  const questionnaireItemText: SxProps<Theme> = {
    fontSize: '13px',
    flex: 1,
  }

  const badgeBase: SxProps<Theme> = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#fff !important',
    padding: '2px 8px',
    borderRadius: '4px',
    minWidth: '36px',
    textAlign: 'center',
    flexShrink: 0,
  }

  const badgeRed: SxProps<Theme> = {
    ...badgeBase,
    backgroundColor: '#ef4444',
  }

  const badgeYellow: SxProps<Theme> = {
    ...badgeBase,
    backgroundColor: '#f59e0b',
  }

  const badgeGreen: SxProps<Theme> = {
    ...badgeBase,
    backgroundColor: '#22c55e',
  }

  const paginationContainer: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 12px 0',
    mt: 'auto',
  }

  const paginationButton: SxProps<Theme> = {
    minWidth: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 500,
    color: color.color20,
    cursor: 'pointer',
    padding: '0 6px',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: `${color.color10}15`,
      borderRadius: '50%',
    },
  }

  const paginationPage: SxProps<Theme> = {
    ...paginationButton,
    fontWeight: 400,
  }

  const paginationPageActive: SxProps<Theme> = {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontSize: '14px',
    fontWeight: 600,
    color: color.color20,
    cursor: 'pointer',
    backgroundColor: color.color17,
  }

  const paginationDisabled: SxProps<Theme> = {
    minWidth: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    fontSize: '13px',
    color: `${color.color10}60`,
    cursor: 'default',
    padding: '0 6px',
  }

  return {
    tabsContainer,
    tabButton,
    tabButtonActive,
    resultsLayout,
    questionnaireListPanel,
    questionnaireListHeader,
    questionnaireListSearch,
    questionnaireItem,
    questionnaireItemActive,
    questionnaireItemSelected,
    questionnaireItemText,
    badgeRed,
    badgeYellow,
    badgeGreen,
    paginationContainer,
    paginationButton,
    paginationPage,
    paginationPageActive,
    paginationDisabled,
  }
}
